'use client';

import { useSpmDomAttr } from '@/reporting/spm';
import { cloneElement, type ReactElement, useMemo, useState } from 'react';

import { getSiteRedirectEncodeUrlWithReferrer } from '@/hooks/useSiteRedirect';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';

interface CountrySiteList2026Props {
  onCountryClick?: (shortName: string) => void;
  deviceType: EPlatform;
  /** Data SPM DOM 打标：shortName → footer_country_sites.country_site_entry_00X；表外返回 undefined 不打标。 */
  resolveSpmCnt?: (shortName: string) => string | undefined;
}

/**
 * 循环内挂载点 Data SPM DOM 打标：hook 不能在 map 中调用，封为小组件把 data-spm
 * 克隆到唯一真实子元素上（不新增 DOM 层级）；开关关闭或编码表外时原样渲染，零 DOM 差异。
 */
function SpmDomNode({ code, children }: { code: string | undefined; children: ReactElement }) {
  const domSpm = useSpmDomAttr(code);
  return domSpm ? cloneElement(children, domSpm) : children;
}

const isCurrentSite = (url: string) => url.includes(env.NEXT_PUBLIC_FULL_URL);

export default function CountrySiteList2026({
  onCountryClick,
  deviceType,
  resolveSpmCnt,
}: CountrySiteList2026Props) {
  const isWeb = deviceType === EPlatform.WEB;
  const countrySiteList = useStore((state) => state.countrySiteList);
  const enabledCountrySites = useMemo(
    () => countrySiteList?.filter((site) => site.status === 1) || [],
    [countrySiteList],
  );
  const [activeCountry, setActiveCountry] = useState('');
  if (enabledCountrySites.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-[12px] text-[14px] leading-[17px] text-white">
      <span className="shrink-0 font-br">Region:</span>
      <div className="notranslate flex items-center gap-[12px]" translate="no">
        {enabledCountrySites.map((site, index) => {
          const siteUrl = isWeb ? site.pc_site_url : site.m_site_url;
          const isActive = activeCountry === site.country_short_name || isCurrentSite(siteUrl);

          return (
            <SpmDomNode
              key={site.country_short_name || index}
              code={onCountryClick ? resolveSpmCnt?.(site.country_short_name) : undefined}
            >
              <a
                href={siteUrl}
                aria-current={isActive ? 'page' : undefined}
                className={`cursor-pointer whitespace-nowrap pb-[2px] text-white no-underline ${
                  isActive ? 'border-b border-white font-bb' : 'font-br'
                }`}
                onClick={(event) => {
                  event.preventDefault();
                  onCountryClick?.(site.country_short_name);
                  setActiveCountry(site.country_short_name);
                  window.setTimeout(
                    () => window.open(getSiteRedirectEncodeUrlWithReferrer(siteUrl), '_self'),
                    500,
                  );
                }}
              >
                {site.country_short_name}
              </a>
            </SpmDomNode>
          );
        })}
      </div>
    </div>
  );
}
